import { apiFetch } from '../../services/api'

export type PlayMadeBy = 'user' | 'strategy'

export interface StrategyPlaySummary {
  id: string
  name: string
  why: string | null
  made_by: PlayMadeBy | null
  kept: number
  excluded: number
  drafts: number
}

export interface StrategyRunRow {
  id: string
  status: string
  run_source: string | null
  created_at: string | null
  finished_at: string | null
  summary: string
}

export interface StrategyPlayRun extends StrategyRunRow {
  play_id: string
  name: string
  // It stopped because the task bucket was full; it runs again when the bucket has room.
  bucket_full?: boolean
  writers: number
  helpers: number
}

export interface StrategyRunTree extends StrategyRunRow {
  helpers: number
  plays: StrategyPlayRun[]
}

export type StrategyFactTopic =
  | 'what_you_sell'
  | 'who_buys'
  | 'personas'
  | 'competitors'
  | 'signals'
  | 'customer_definition'
  | 'ownership'

export interface StrategyFact {
  id: string
  topic: StrategyFactTopic
  text: string
  source: string | null
  run_id?: string | null
  created_at?: string | null
}

export interface TaskBucket {
  size: number
  open: number
}

export interface Strategy {
  // The latest Strategy run.
  run: StrategyRunTree | null
  runs: StrategyRunRow[]
  plays: StrategyPlaySummary[]
  // Who the plays go after, in one sentence, from the latest run that said so.
  focus: string | null
  drafts_waiting: number
  task_bucket: TaskBucket
  facts?: StrategyFact[]
  // Connected CRMs, e.g. ['salesforce'].
  connected?: string[]
}

export interface StrategyRunAccount {
  why?: string
  evidence?: string[]
  uncertain?: string
}

// A play run as one Strategy run's page shows it: what it produced in that run.
export interface StrategyRunPlay extends StrategyPlayRun {
  why: string | null
  made_by: PlayMadeBy | null
  kept: number
  // Every prospect the play has, from any run.
  total_kept: number
  excluded: number
  drafts: number
}

export interface StrategyRunDetailRun extends StrategyRunRow {
  first_run: boolean
  helpers: number
  account: StrategyRunAccount | null
  plays: StrategyRunPlay[]
}

export type StrategyChangeKind = 'create_play' | 'edit_play' | 'retire_play' | 'edit_sequence'

export interface StrategyRunChange {
  id: string
  kind: StrategyChangeKind
  play_id: string | null
  name: string | null
  reason: string | null
}

export interface StrategyRunCounts {
  tasks: number
  accounts: number
  prospects: number
}

export interface StrategyRunDetail {
  run: StrategyRunDetailRun
  counts: StrategyRunCounts
  // The facts this run learned.
  facts: (StrategyFact & { replaced: boolean })[]
  changes: StrategyRunChange[]
  connected: string[]
  // How often the run read each source.
  sources: Partial<Record<'web' | 'crm' | 'leadiq', number>>
  task_bucket: TaskBucket
}

export interface StrategyRunStart {
  run_id: string
  started: boolean
}

// A play's own run, as its play page lists it.
export interface PlayPageRun {
  id: string
  status: string
  created_at: string | null
  started_at: string | null
  finished_at: string | null
  status_summary: string
  account?: StrategyRunAccount | null
  writers: number
  helpers: number
}

export interface StrategyPlayStep {
  step_type: string
  wait_days?: number | null
}

export interface StrategyPlayChange {
  id: string
  kind: string
  created_at: string | null
  // The person who made the change; null when the Strategy agent made it.
  by: { id: string; name: string } | null
  reason: string | null
  steps: StrategyPlayStep[] | null
  removed: boolean
}

export interface StrategyPlayDetails {
  id: string
  name: string
  status: string
  brief: string
  notes: string
  description: string
  made_by: PlayMadeBy | null
  counts: { kept: number; excluded: number; drafts: number }
  runs: PlayPageRun[]
  changes: StrategyPlayChange[]
}

export interface AgentRunEvent {
  id: string
  kind: string
  message: string | null
  payload: Record<string, any>
  created_at: string | null
}

export function fetchStrategy(): Promise<Strategy> {
  return apiFetch('/strategy')
}

export function fetchStrategyRun(runId: string): Promise<StrategyRunDetail> {
  return apiFetch(`/strategy/runs/${encodeURIComponent(runId)}`)
}

// Joins the run already queued or running instead of starting a second one.
export function startStrategyRun(onboarding = false): Promise<StrategyRunStart> {
  return apiFetch('/strategy/run', { method: 'POST', body: { onboarding } })
}

export function fetchAgentRunEvents(runId: string, limit = 200): Promise<{ events: AgentRunEvent[] }> {
  return apiFetch(`/agent_runs/${encodeURIComponent(runId)}/events?limit=${limit}`)
}

const enc = encodeURIComponent

export function createStrategyPlay(name: string, brief: string): Promise<{ play: { id: string; name: string }; run_id: string }> {
  return apiFetch('/strategy/plays', { method: 'POST', body: { name, brief } })
}

export function fetchStrategyPlay(playId: string): Promise<StrategyPlayDetails> {
  return apiFetch(`/strategy/plays/${enc(playId)}`)
}

export function saveStrategyPlayBrief(playId: string, brief: string, run: boolean): Promise<{ run_id: string | null }> {
  return apiFetch(`/strategy/plays/${enc(playId)}/brief`, { method: 'PUT', body: { brief, run } })
}

export function runStrategyPlay(playId: string): Promise<{ run_id: string }> {
  return apiFetch(`/strategy/plays/${enc(playId)}/run`, { method: 'POST' })
}
